import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plane, MapPin, FileText, Wallet, Home, Snowflake, CreditCard, AlertTriangle,
  CheckCircle2, Send, Loader2, PartyPopper, CalendarDays, ShieldCheck, ArrowLeft,
  MessageCircle, BadgeCheck, Info, Users, FileDown,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { MONTHS, COUNTRIES, WHATSAPP_LINK, FLIGHT_OPTIONS, FLIGHT_OPTIONS_INFO, NATIONALITIES, toPLN, toMXN } from "@/data/constants";
import { Footer } from "@/components/site/Footer";

const IMAGES = {
  flight: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  city: "https://images.unsplash.com/photo-1772012454161-c4e0c6a58f70?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  apartment: "https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  winter: "https://images.unsplash.com/photo-1613052903572-fe85e50d5d46?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const inputCls =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-[#0B132B] placeholder:text-slate-400 focus:outline-none focus:border-[#FFD166] transition-colors";
const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";

const Card = ({ icon: Icon, title, children, tone = "light" }) => (
  <div className={`rounded-2xl p-6 card-hover border ${tone === "dark" ? "bg-[#1C2541] border-white/10" : "bg-white border-slate-200"}`}>
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-[#FFD166]/15 flex items-center justify-center text-[#B8860B] shrink-0"><Icon size={20} /></span>
      <h3 className={`font-head font-bold text-lg ${tone === "dark" ? "text-white" : "text-[#0B132B]"}`}>{title}</h3>
    </div>
    <div className={`mt-3 text-sm leading-relaxed ${tone === "dark" ? "text-slate-300" : "text-slate-600"}`}>{children}</div>
  </div>
);

export default function NextStep() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state || {};
  const [form, setForm] = useState({
    full_name: prefill.full_name || "", country_code: prefill.country_code || "+52",
    phone: prefill.phone || "", email: prefill.email || "", address: "", colonia: "",
    postal_code: "", city: prefill.city || "", state: "", references: "", travel_month: "", flight_option: "", oct3: "",
    passport_number: "", passport_expiry: "", birth_date: "", nationality: "Mexicana", curp: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toDMY = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return d && m && y ? `${d}/${m}/${y}` : iso; };
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const lookupCP = async (cp) => {
    if (!/^\d{5}$/.test(cp)) return;
    setCpLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/MX/${cp}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          const stateRaw = place["state"] || "";
          const stateNorm = /distrito federal|ciudad de m|cdmx/i.test(stateRaw) ? "Ciudad de México" : stateRaw;
          const isCDMX = stateNorm === "Ciudad de México";
          const placeName = place["place name"] || "";
          setForm((f) => ({
            ...f,
            // En CDMX zippopotam devuelve la colonia en "place name"; en otros estados devuelve el municipio.
            colonia: isCDMX ? (placeName || f.colonia) : "",
            city: isCDMX ? "Ciudad de México" : (placeName || f.city),
            state: stateNorm || f.state,
          }));
        }
      }
    } catch (e) { /* silencioso: el usuario puede escribir manualmente */ }
    finally { setCpLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Completa tu nombre y teléfono.");
      return;
    }
    if (!form.oct3) {
      toast.error("Indícanos si quieres reservar tu lugar o si prefieres coordinar otra fecha.");
      return;
    }
    if (form.oct3 === "other" && !form.travel_month) {
      toast.error("Elige el mes en el que podrías viajar.");
      return;
    }
    if (!form.flight_option) {
      toast.error("Elige cómo quieres tu vuelo (financiado 100%, 50% o pagado por ti).");
      return;
    }
    if (!accepted) {
      toast.error("Debes confirmar que leíste la información y estás dispuesto/a a viajar.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/trip-confirmation", {
        ...form, on_next_flight: form.oct3 === "yes", accepted,
        birth_date: toDMY(form.birth_date),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#0B132B] min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B132B]/95 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nextstep-logo" className="flex items-center gap-2 text-white">
            <ArrowLeft size={18} /> <span className="text-xl font-head font-extrabold">europe<span className="text-[#FFD166]">jobs</span></span>
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="nextstep-wa-top"
            className="flex items-center gap-2 bg-[#25D366] text-white font-semibold text-sm px-4 py-2 rounded-full">
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="nextstep-hero" className="relative pt-16 pb-14 md:pt-24 md:pb-20 text-center overflow-hidden ej-grain">
        <div className="orb w-[420px] h-[420px] bg-[#FFD166] -top-24 left-1/2 -translate-x-1/2 opacity-15" />
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <span className="inline-flex items-center gap-2 bg-[#FFD166] text-[#0B132B] px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest">Etapa 2</span>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 text-white leading-tight">
            ¡Felicidades{prefill.full_name ? `, ${String(prefill.full_name).split(" ")[0]}` : ""}! 🎉
          </h1>
          <p className="text-slate-300 text-base md:text-lg mt-5">
            Gracias por llegar hasta aquí. Ya diste un gran paso y ahora viene la siguiente etapa de tu aventura.
            Lee toda la información y, al final, confirma que estás dispuesto/a a viajar.
          </p>
        </div>
      </section>

      {/* Banner image */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-6">
        <img src={IMAGES.flight} alt="Vuelo a Europa" className="w-full h-48 md:h-64 object-cover rounded-3xl border border-white/10" />
      </div>

      {/* Flight routes */}
      <section className="pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#12203f] border border-white/10 rounded-3xl p-6 md:p-8">
            <div className="flex items-center gap-3 text-[#FFD166]">
              <Plane size={22} /> <span className="font-head font-bold text-lg text-white">Nuestras rutas de vuelo</span>
            </div>
            <p className="text-slate-300 text-sm mt-2">Tenemos programadas <strong className="text-white">varias rutas desde diferentes países</strong>. Un asesor programará tu vuelo exacto en los meses elegidos.</p>
            <div className="mt-5 grid gap-3">
              {[
                ["México 🇲🇽", "Ámsterdam 🇳🇱", "Polonia 🇵🇱"],
                ["Argentina 🇦🇷", "México 🇲🇽", "Polonia 🇵🇱"],
                ["República Dominicana 🇩🇴", "Polonia 🇵🇱"],
              ].map((route, ri) => (
                <div key={ri} className="flex flex-wrap items-center gap-2 bg-[#0B132B] rounded-2xl px-4 py-3 border border-white/5">
                  {route.map((stop, si) => (
                    <React.Fragment key={stop}>
                      <span className="font-head font-bold text-white text-sm sm:text-base">{stop}</span>
                      {si < route.length - 1 && <Plane className="text-[#FFD166]" size={15} />}
                    </React.Fragment>
                  ))}
                </div>
              ))}
              <div className="text-center text-xs text-slate-400">…y más rutas disponibles según tu país.</div>
            </div>
            <p className="text-center text-sm text-slate-400 mt-4">
              Un asesor te confirmará por WhatsApp <strong className="text-slate-200">el día exacto de salida y desde qué aeropuerto</strong> viajarás.
            </p>
          </div>
        </div>
      </section>

      {/* Next flight alert */}
      <section className="pb-2 pt-2">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#E63946] to-[#b0202f] rounded-3xl p-6 md:p-8 text-white">
            <div className="flex items-center gap-2 text-[#FFD166] font-bold uppercase tracking-wider text-xs"><CalendarDays size={16} /> Próximos vuelos</div>
            <h3 className="font-head font-extrabold text-2xl md:text-3xl mt-2">Varias rutas programadas en los próximos meses</h3>
            <p className="text-white/90 mt-2 text-sm md:text-base">
              Contamos con salidas desde <strong>varios países</strong>. Un <strong>asesor</strong> programará tu vuelo exacto dentro de los meses que elijas y te dirá <strong>desde qué aeropuerto</strong> saldrás.
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5 text-sm">
              <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><Plane size={14} /> Varias fechas</span>
              <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><Users size={14} /> Lugares limitados</span>
              <span className="bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5"><CalendarDays size={14} /> Un asesor confirma tu fecha</span>
            </div>
            <p className="text-white/80 text-xs mt-4 flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              Si no estás completamente seguro/a, por favor no lo aceptes: los lugares son limitados y otra persona podría necesitarlo.
            </p>
          </div>
        </div>
      </section>

      {/* Info grid */}
      {/* SHEIN Wrocław offer */}
      <section className="pb-2 pt-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#12203f] border border-[#FFD166]/30 rounded-3xl overflow-hidden grid md:grid-cols-2">
            <div className="relative h-56 md:h-auto">
              <img src="https://static.prod-images.emergentagent.com/jobs/a7fe85d0-fdd8-482d-bdb1-ace7855a175a/images/0282b95c9b85dc982e275e00750ab9cb1fb5a2216d7dd4d45e8ca8c41cb6c4fc.jpeg"
                alt="Almacén de SHEIN en Wrocław, Polonia" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#12203f] via-transparent to-transparent" />
            </div>
            <div className="p-6 md:p-8">
              <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#FFD166] text-[#0B132B] px-2.5 py-1 rounded-full">Oferta destacada</span>
              <h3 className="font-head font-extrabold text-2xl md:text-3xl text-white mt-3">SHEIN · Wrocław 🇵🇱</h3>
              <p className="text-slate-300 text-sm mt-2">Trabaja en el <strong className="text-white">almacén de SHEIN en Wrocław</strong>, Polonia, en el área de <strong className="text-white">logística</strong>. Un empleo estable, con buen sueldo y alojamiento incluido.</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-200">
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Sueldo promedio de <strong className="text-white">30,000 MXN o más</strong> al mes</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Alojamiento moderno (primer mes gratis)</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Vuelo financiado hasta el 100%</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Domingo libre y posibilidad de dos días de descanso</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-head font-extrabold text-2xl md:text-4xl text-white text-center">Lo que debes saber</h2>
          <div className="ej-accent-bar mx-auto mt-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            <Card icon={MapPin} title="¿Qué necesitas hacer?" tone="dark">
              Debes llegar al <strong className="text-white">aeropuerto de salida</strong> que te indique tu asesor para tomar el vuelo. No importa en qué ciudad o estado
              estés: lo importante es estar a tiempo el día de salida. Te ayudamos a encontrar la mejor ruta (avión, autobús, etc.).
            </Card>
            <Card icon={Wallet} title="Costos en México" tone="dark">
              Llegar al aeropuerto de salida (tu asesor te dirá cuál) cuesta aprox. <strong className="text-white">$500 a $1,500 MXN</strong>,
              dependiendo de dónde te encuentres. Este costo corre por tu cuenta; el viaje internacional lo financiamos.
            </Card>
            <Card icon={FileText} title="Documentos necesarios" tone="dark">
              <ul className="space-y-1.5">
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Pasaporte vigente</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Compromiso de viaje firmado</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Invitación de la empresa firmada</li>
                <li className="flex gap-2"><BadgeCheck size={16} className="text-[#FFD166] shrink-0 mt-0.5" /> Cláusula de responsabilidad firmada</li>
              </ul>
            </Card>
            <Card icon={Plane} title="Costos del viaje: 8,000 PLN" tone="dark">
              El costo total del viaje es de <strong className="text-white">8,000 PLN (≈ {toMXN(8000)})</strong> e incluye <strong className="text-white">seguro de viaje, boleto de avión México → Europa, boleto hacia tu destino final y los costos del trámite</strong>.
              <div className="mt-3 space-y-2">
                <div className="bg-[#0B132B] rounded-lg px-3 py-2"><strong className="text-white">Si financias el viaje:</strong> los 8,000 PLN se descuentan de tu sueldo en los <strong className="text-white">primeros 3 meses</strong>, en 3 pagos de ~<strong className="text-white">2,667 PLN (≈ {toMXN(2667)})</strong> cada uno.</div>
                <div className="bg-[#0B132B] rounded-lg px-3 py-2"><strong className="text-white">Ejemplo:</strong> ganando en promedio <strong className="text-white">30,000 MXN o más</strong> al mes los primeros 3 meses, cubres los 8,000 PLN divididos en 3 pagos y aún te queda sueldo cada mes.</div>
                <div className="bg-[#0B132B] rounded-lg px-3 py-2"><strong className="text-white">Si lo pagas tú:</strong> cubres los 8,000 PLN (≈ {toMXN(8000)}) por adelantado y todo lo que ganes es para ti desde el día 1.</div>
              </div>
            </Card>
            <Card icon={CreditCard} title="Pagos de la empresa" tone="dark">
              La empresa paga el <strong className="text-white">día 15 de cada mes</strong>. Ejemplo: si empiezas a trabajar el día 1,
              recibes tu primer pago el día 15.
            </Card>
            <Card icon={Home} title="Alojamiento" tone="dark">
              El <strong className="text-white">primer mes NO se paga</strong>. A partir del segundo mes son <strong className="text-white">650 PLN</strong>
              (~$3,000 – $3,500 MXN). Es un departamento moderno compartido, con Wi-Fi, cocina equipada, lavadora y ambiente seguro.
            </Card>
            <Card icon={Wallet} title="Dinero para tus gastos" tone="dark">
              Lleva unos <strong className="text-white">$5,000 MXN</strong> para tus primeras semanas (comida, transporte, etc.),
              ya que tu primer pago lo recibes hasta el día 15.
            </Card>
            <Card icon={CreditCard} title="Tu sueldo y descuentos" tone="dark">
              Se gana un promedio de <strong className="text-white">250 zł al día</strong> (aprox. <strong className="text-white">$1,150 – $1,200 MXN</strong>), que con horas extra suele sumar <strong className="text-white">30,000 MXN o más al mes</strong>. <strong className="text-white">¡Vale la pena ir a Polonia!</strong>
              Se descuenta el alojamiento (desde el 2º mes) y, si financias el viaje, los <strong className="text-white">8,000 PLN</strong> se descuentan en los <strong className="text-white">primeros 3 meses</strong> (3 pagos de ~2,667 PLN).
            </Card>
            <Card icon={CalendarDays} title="Jornada y descanso" tone="dark">
              Turnos de <strong className="text-white">10 a 12 horas diarias</strong>. Se trabaja de <strong className="text-white">lunes a sábado</strong>, con el <strong className="text-white">domingo libre</strong> y, en muchos casos, la posibilidad de tener <strong className="text-white">dos días libres</strong>.
            </Card>
            <Card icon={Snowflake} title="Clima en Polonia" tone="dark">
              En octubre, noviembre y diciembre hace frío (entre <strong className="text-white">-5°C y 10°C</strong>). Lleva ropa de invierno:
              chamarra gruesa, suéteres, ropa térmica, gorro, guantes, bufanda y calzado adecuado.
            </Card>
          </div>

          {/* Gallery */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { img: IMAGES.city, cap: "Ciudades como Wrocław te esperan" },
              { img: IMAGES.apartment, cap: "Departamentos modernos y compartidos" },
              { img: IMAGES.winter, cap: "Inviernos con nieve: lleva ropa abrigadora" },
            ].map((gi, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden group border border-white/10">
                <img src={gi.img} alt={gi.cap} className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/20 to-transparent" />
                <div className="absolute bottom-0 p-4 text-white text-sm font-semibold">{gi.cap}</div>
              </div>
            ))}
          </div>

          {/* Important warning */}
          <div className="mt-8 bg-[#E63946]/15 border border-[#E63946]/40 rounded-2xl p-6 flex gap-4">
            <AlertTriangle className="text-[#FF8A8A] shrink-0" size={26} />
            <div>
              <div className="font-head font-bold text-white text-lg">Si no estás seguro/a de viajar, no firmes.</div>
              <p className="text-sm text-slate-300 mt-2">
                No es un viaje a un pueblo cercano: estás tomando la decisión de ir a Europa, al otro lado del mundo. Esto implica costos
                y una organización seria. Queremos que tomes esta decisión de forma informada, clara y consciente. Si tienes dudas,
                nuestro equipo está para ayudarte.
              </p>
            </div>
          </div>

          {/* Documentos + WhatsApp */}
          <div className="mt-6 bg-[#12203f] border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Send className="text-[#FFD166] shrink-0" size={26} />
            <div className="flex-1">
              <div className="font-head font-bold text-white text-lg">Firma tus documentos en la web o imprímelos</div>
              <p className="text-sm text-slate-300 mt-1">
                Una vez que leas y confirmes esta información, te compartiremos tus <strong className="text-white">3 documentos</strong>: el <strong className="text-white">compromiso de viaje</strong>, la <strong className="text-white">invitación de la empresa</strong> y la <strong className="text-white">cláusula de responsabilidad</strong>. Puedes firmarlos directamente <strong className="text-white">en la web</strong> o, si lo prefieres, <strong className="text-white">imprimirlos, firmarlos a mano y enviarnos una foto o escaneo</strong>. El <strong className="text-white">contrato final</strong>, con todas las cláusulas ya leídas, se firma al llegar a Polonia. ¿Tienes dudas? Escríbenos por WhatsApp.
              </p>
            </div>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="nextstep-wa-doubts"
              className="flex items-center gap-2 bg-[#25D366] text-white font-bold px-5 py-3 rounded-full whitespace-nowrap">
              <MessageCircle size={16} /> Tengo dudas
            </a>
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="py-12 md:py-16 bg-[#0d1730]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-head font-extrabold text-2xl md:text-4xl text-white text-center">¿Cómo es el proceso?</h2>
          <div className="ej-accent-bar mx-auto mt-4" />
          <div className="mt-10 space-y-4">
            {[
              { t: "Recibes tus 3 documentos", d: "Te compartimos el compromiso de viaje, la invitación de la empresa y la cláusula de responsabilidad." },
              { t: "Fírmalos en la web o imprímelos", d: "Puedes firmarlos directamente en la web, o imprimirlos y firmarlos a mano. Léelos con calma antes de firmar." },
              { t: "Si los imprimes, envíanoslos", d: "Toma una foto clara o escanea los documentos firmados y envíanoslos por WhatsApp o correo. Si firmaste en la web, no necesitas hacer nada más." },
              { t: "Envíanos prueba de tu traslado al aeropuerto de salida", d: "Comparte tu boleto de avión o autobús hacia el aeropuerto que te indique tu asesor, desde donde saldrá tu vuelo a Europa." },
              { t: "Un agente te recibe en el aeropuerto", d: "El día del vuelo, un agente te verá en el aeropuerto de salida para confirmar tus datos y darte la información relevante antes de partir." },
              { t: "Firmas el contrato final en Polonia", d: "Al llegar firmarás el contrato de trabajo definitivo, con todas las cláusulas ya leídas y explicadas." },
            ].map((s, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="flex gap-4 bg-[#1C2541] border border-white/10 rounded-2xl p-5">
                <span className="w-9 h-9 rounded-full bg-[#FFD166] text-[#0B132B] font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                <div>
                  <div className="font-head font-bold text-white">{s.t}</div>
                  <div className="text-sm text-slate-300 mt-1">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-400 text-xs mt-6 flex items-center justify-center gap-1.5"><Info size={13} /> ¿Dudas con algún paso? Escríbenos por WhatsApp y te guiamos.</p>
        </div>
      </section>

      {/* Download PDF */}
      <section className="pb-12 md:pb-16 bg-[#0d1730]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#12203f] border border-[#FFD166]/30 rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <span className="w-14 h-14 rounded-2xl bg-[#FFD166]/15 flex items-center justify-center text-[#FFD166] shrink-0"><FileText size={26} /></span>
            <div className="flex-1">
              <div className="font-head font-bold text-white text-lg">Descarga toda la información (Etapas 2 a 5)</div>
              <p className="text-sm text-slate-300 mt-1">Guarda el documento completo en tu celular y léelo con calma, sin prisas, cuando quieras.</p>
            </div>
            <a href="/etapas-europejobs.pdf" download target="_blank" rel="noreferrer" data-testid="download-pdf-btn"
              className="flex items-center gap-2 bg-[#FFD166] text-[#0B132B] font-bold px-6 py-3.5 rounded-full whitespace-nowrap hover:bg-[#E0A926] transition-colors">
              <FileDown size={18} /> Descargar PDF
            </a>
          </div>
        </div>
      </section>

      {/* Confirmation form */}
      <section id="confirmar" className="py-14 md:py-20 bg-[#F5F7FB]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              data-testid="nextstep-success" className="bg-white border border-[#16a34a]/40 rounded-3xl p-10 text-center shadow-lg">
              <PartyPopper className="mx-auto text-[#16a34a]" size={56} />
              <h2 className="font-head font-extrabold text-3xl mt-6 text-[#0B132B]">
                ¡Gracias{prefill.full_name ? `, ${String(prefill.full_name).split(" ")[0]}` : ""}! 🎉
              </h2>
              <p className="text-slate-600 mt-3">
                <strong>Si completaste tus datos correctamente</strong>, un asesor se pondrá en contacto contigo muy pronto para <strong>acordar tu fecha de vuelo</strong> y explicarte los <strong>documentos que tendrás que firmar antes de viajar</strong>. ¡Prepárate para tu nueva aventura en Polonia! 🇵🇱
              </p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white font-bold px-7 py-3.5 rounded-full">
                <MessageCircle size={18} /> Escríbenos por WhatsApp
              </a>
            </motion.div>
          ) : (
            <>
              <div className="text-center mb-8">
                <span className="text-[#E63946] text-sm font-semibold uppercase tracking-wider">Confirma tu decisión</span>
                <h2 className="font-head font-extrabold text-3xl md:text-4xl mt-2 text-[#0B132B]">Estoy listo/a para viajar</h2>
                <p className="text-slate-600 mt-3">Escribe tus datos y tu domicilio, e indica en qué mes puedes viajar.</p>
              </div>
              <form onSubmit={submit} data-testid="trip-confirm-form" className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 md:p-9 space-y-5">
                <div>
                  <label className={labelCls}>Nombre y apellidos *</label>
                  <input data-testid="tc-name" className={inputCls} value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)} placeholder="Ej. Juan Pérez García" />
                </div>
                <div>
                  <label className={labelCls}>Teléfono / WhatsApp *</label>
                  <div className="flex gap-2">
                    <select data-testid="tc-code" className={`${inputCls} w-28`} value={form.country_code}
                      onChange={(e) => set("country_code", e.target.value)}>
                      {COUNTRIES.map((c) => <option key={c.code + c.name} value={c.code}>{c.code}</option>)}
                    </select>
                    <input data-testid="tc-phone" className={inputCls} value={form.phone}
                      onChange={(e) => set("phone", e.target.value)} placeholder="55 1234 5678" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Correo electrónico</label>
                  <input data-testid="tc-email" type="email" className={inputCls} value={form.email}
                    onChange={(e) => set("email", e.target.value)} placeholder="tucorreo@ejemplo.com" />
                </div>

                <div>
                  <label className={labelCls}>Nacionalidad *</label>
                  <select data-testid="tc-nationality" className={inputCls}
                    value={NATIONALITIES.includes(form.nationality) ? form.nationality : "Otra"}
                    onChange={(e) => set("nationality", e.target.value === "Otra" ? "" : e.target.value)}>
                    {NATIONALITIES.map((n) => <option key={n} value={n}>{n === "Otra" ? "Otra…" : n}</option>)}
                  </select>
                  {!NATIONALITIES.includes(form.nationality) && (
                    <input data-testid="tc-nationality-other" className={`${inputCls} mt-2`} value={form.nationality}
                      onChange={(e) => set("nationality", e.target.value)} placeholder="Escribe tu nacionalidad" />
                  )}
                </div>
                <p className="text-xs text-slate-400 -mt-2">No necesitas tu pasaporte todavía: un asesor te ayudará con los documentos más adelante.</p>

                <div>
                  <label className={labelCls}>¿Cuándo quieres tu vuelo? *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button type="button" data-testid="tc-oct3-yes" onClick={() => { set("oct3", "yes"); set("travel_month", "Octubre"); }}
                      className={`text-left rounded-xl p-4 border transition-all ${form.oct3 === "yes" ? "border-[#16a34a] bg-[#eafaf0]" : "border-slate-300 bg-white hover:border-slate-400"}`}>
                      <div className="font-head font-bold text-sm text-[#0B132B] flex items-center gap-2"><CheckCircle2 size={16} className="text-[#16a34a]" /> Quiero mi vuelo en octubre</div>
                      <div className="text-xs text-slate-500 mt-1">Un asesor me confirmará el día exacto y el aeropuerto de salida.</div>
                    </button>
                    <button type="button" data-testid="tc-oct3-other" onClick={() => { set("oct3", "other"); set("travel_month", ""); }}
                      className={`text-left rounded-xl p-4 border transition-all ${form.oct3 === "other" ? "border-[#FFD166] bg-[#FFF6DC]" : "border-slate-300 bg-white hover:border-slate-400"}`}>
                      <div className="font-head font-bold text-sm text-[#0B132B] flex items-center gap-2"><CalendarDays size={16} className="text-[#B8860B]" /> Prefiero elegir otro mes</div>
                      <div className="text-xs text-slate-500 mt-1">Elige abajo el mes en el que quieres viajar.</div>
                    </button>
                  </div>
                </div>

                {form.oct3 === "other" && (
                  <div>
                    <label className={labelCls}>¿En qué mes estás dispuesto/a a viajar? *</label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {MONTHS.map((m) => (
                        <button type="button" key={m} data-testid={`tc-month-${m}`} onClick={() => set("travel_month", m)}
                          className={`px-2 py-2.5 rounded-lg text-xs font-medium border transition-all ${form.travel_month === m ? "border-[#FFD166] bg-[#FFD166] text-[#0B132B]" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5"><Info size={13} className="shrink-0 mt-0.5" /> Al elegir otro mes, un agente se pondrá en contacto contigo para coordinar la fecha exacta de tu vuelo.</p>
                  </div>
                )}

                <div>
                  <label className={labelCls}>¿Cómo quieres tu vuelo? *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {["financiado_100", "financiado_50", "pagado_trabajador"].map((value) => {
                      const o = FLIGHT_OPTIONS_INFO[value];
                      return (
                        <button type="button" key={value} data-testid={`tc-flight-${value}`} onClick={() => set("flight_option", value)}
                          className={`text-left rounded-xl p-4 border transition-all ${form.flight_option === value ? "border-[#FFD166] bg-[#FFF6DC]" : "border-slate-300 bg-white hover:border-slate-400"}`}>
                          <div className="font-head font-bold text-sm text-[#0B132B]">{o.title}</div>
                          <div className="text-xs text-slate-500 mt-1">{o.short}</div>
                        </button>
                      );
                    })}
                  </div>
                  {form.flight_option && FLIGHT_OPTIONS_INFO[form.flight_option] && (
                    <div data-testid="tc-flight-summary" className="mt-3 rounded-2xl border border-[#FFD166]/50 bg-[#FFFBF0] p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-bold text-sm text-[#0B132B]">{FLIGHT_OPTIONS_INFO[form.flight_option].title}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-[#0B132B] text-[#FFD166] px-2 py-0.5 rounded-full">{FLIGHT_OPTIONS_INFO[form.flight_option].badge}</span>
                      </div>
                      <ul className="mt-2.5 space-y-1.5">
                        {FLIGHT_OPTIONS_INFO[form.flight_option].lines.map((ln, i) => (
                          <li key={i} className="flex gap-2 text-sm text-slate-700">
                            <BadgeCheck size={15} className="text-[#16a34a] shrink-0 mt-0.5" /> <span>{ln}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[11px] text-slate-500 mt-2.5 flex items-start gap-1.5">
                        <Info size={12} className="shrink-0 mt-0.5" /> Equivalencias en pesos calculadas al tipo de cambio actual (1 PLN ≈ {toMXN(1).replace(" MXN", "")} MXN). El monto elegido se refleja en tus documentos.
                      </p>
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer bg-[#F5F7FB] rounded-xl p-4 border border-slate-200">
                  <input type="checkbox" data-testid="tc-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-0.5 accent-[#FFD166] w-4 h-4" />
                  <span>Confirmo que <strong>leí toda la información</strong> de esta página y que estoy <strong>dispuesto/a a viajar</strong> a Polonia.</span>
                </label>

                <button type="submit" disabled={submitting} data-testid="tc-submit"
                  className="w-full bg-[#0B132B] text-white font-bold py-4 rounded-full hover:bg-[#12203f] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} className="text-[#FFD166]" />}
                  Confirmar y continuar
                </button>
                <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5"><ShieldCheck size={13} /> Tus datos son confidenciales y solo se usan para tu proceso.</p>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
